import React, { useState, useContext, useEffect, useMemo } from 'react'
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import styles from './ProfilePublishButton.module.css'
import { ConfirmModal } from '@project/components';
import axios from '../../utils/axios';
import { RootContext } from '../../hoc/RootContext/RootContext';
import validationSchema from '../BasicInfoForm/schema';
import { PUBLISHED, UNPUBLISHED, DRAFT, TEAR_SHEET } from '../../constants';
import { get } from '../../utils/utils';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';

const classNames = require('classnames');

/** Handles rendering publish/unpublish button, and toggling the space profile status */
export default ({ individualProfile, spaceProfile, setSpaceProfile, errors, setErrors, fieldSettings, floorErrors, setFloorErrors, spaceProfileCollection, validateBasicInfoErrors }) => {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [softRequiredErrors, setSoftRequiredErrors] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isRegeneratingPdf, setIsRegeneratingPdf] = useState(false);
  const { currentUser } = useContext(RootContext);
  const { guid, status } = spaceProfile;

  /**
   * Fetch latitide & longitude using address
   * @param {String} address The profile address
   * @returns { lat, lng }
   */
  const fetchLatLngFromGoogleService = async (address) => {
    const geoData = await axios.get(`/properties/getPropertyLatLngFromAddress/?address=${encodeURIComponent(JSON.stringify(address))}`)
    if (!geoData.data.success) return { lat: null, lng: null }
    return geoData.data
  }

  /**
   * Function to publish the space profile
   * @param {String} status The status to be set
   * @param {String} prevStatus Existing status
   */
  const publishProfile = async (status, prevStatus) => {
    setErrors(null);
    if (!(status === UNPUBLISHED && prevStatus === PUBLISHED)) {
      const validation = validateBasicInfoErrors(validationSchema, fieldSettings, individualProfile);
      const errors = validation.errors;
      const softErrors = validation.softRequiredErrors;
      const totalErrors = [...errors, ...softErrors];

      setErrors(totalErrors);

      if (errors.length) {
        setShowConfirmModal(false);
        return;
      }

      if (!softRequiredErrors.length && softErrors.length) {
        setSoftRequiredErrors(softErrors);
        return;
      }
    }

    if (status === PUBLISHED) individualProfile.preview_enabled = false;

    try {
      setIsSaving(true);
      setShowConfirmModal(false);
      // fetch profile lat & lng while publishing the profile if they don't exist already
      if ((!spaceProfile.latitude || !spaceProfile.longitude) && prevStatus === UNPUBLISHED) {
        const { display_address, address1, city, state, postal } = spaceProfile
        const address = {
          address1: display_address || address1,
          city,
          state,
          postal
        }
        const { lat, lng } = await fetchLatLngFromGoogleService(address)
        if (lat && lng) {
          spaceProfile.latitude = lat
          spaceProfile.longitude = lng
        }
      }
      
      // Updates company settings to ensure fields settings exist when publishing ID360 profile
      const putObj = {
        fieldSettings
      }
      await axios.put(`/company-settings/${spaceProfile.company}`, putObj)

      // Checks to see if multiple levels exist
      // If so then we reassign the profile payload to be an array of all levels and their individual profiles and status changes
      let profilePayload = { ...spaceProfile, status, preview_enabled: individualProfile.preview_enabled}
      if (Object.keys(spaceProfileCollection).length > 1) {
        const spaceProfiles = []
        for (const profile in spaceProfileCollection) {
          const updatedProfile = { ...spaceProfileCollection[profile], status, preview_enabled: individualProfile.preview_enabled };
          spaceProfiles.push(updatedProfile);
        }

        profilePayload = spaceProfiles;
      }

      // Since the status bar is dictated off the first floor we will grab the response from the first floor publish and show that to the end user
      // PDF links will still respect their individual floors
      let result
      if (Array.isArray(profilePayload)) {
        const results = [];
        for (const profile of profilePayload) {
          const response = await axios.put(`/space-profiles/${profile.guid}`, { spaceProfile: profile, publishButton: true });
          results.push(response);
        }
        result = results[0]
      } else {
        result = await axios.put(`/space-profiles/${guid}`, { spaceProfile: profilePayload, publishButton: true });
      }
      spaceProfile.status = status;
      spaceProfile.prevStatus = prevStatus;
      spaceProfile.status_history = result.data.record.status_history;
      setSpaceProfile({ ...spaceProfile });
    } catch (e) {
      setErrors(`${t('error.general')}: ${get(e, 'response.data.errors')[0]}`);
    } finally {
      setIsSaving(false);
      setErrors(null);
      setSoftRequiredErrors([]);
    }
  }

  const closeConfirmModalHandler = () => {
    setShowConfirmModal(false);
    setSoftRequiredErrors([]);
  }

  const publishPreview = () => {
    // Will only show preview link if all floor errors are resolved
    individualProfile.preview_enabled = Object.keys(floorErrors).length === 0
    publishProfile(status, status);
  }

  const publishButtonDisabled = () => {
    if (Object.keys(floorErrors).length !== 0 || isSaving) return true

    return false
  }

  /**
   * Function to regenerate PDF for the published profile
   */
  const regeneratePdf = async () => {
    try {
      setIsRegeneratingPdf(true);
      await toast.promise(axios.post(`/space-profiles/${guid}/generate-pdf`), {
        loading: 'Regenerating PDF...',
        success: 'PDF generation request sent successfully!',
        error: 'Failed to regenerate PDF',
      });
    } catch (e) {
      console.error('Error regenerating PDF:', e);
      setErrors(`${t('error.general')}: Failed to regenerate PDF`);
    } finally {
      setIsRegeneratingPdf(false);
    }
  }
  
  // Side effect logic to call publishPreview on initial load and floor change
  const currentFloor = useMemo(() => {
    return individualProfile.floor || 1
  }, [individualProfile])

  useEffect(() => {
    // Check if currentFloor is defined and errors is not null or an empty array
    if (currentFloor !== undefined && errors !== null) { 
      const updatedFloorErrors = { ...floorErrors };

      if (errors === null || errors.length === 0) {
        delete updatedFloorErrors[currentFloor];
      } else {
        updatedFloorErrors[currentFloor] = errors;
      }

      // Update floorErrors state only if there's a change
      if (JSON.stringify(floorErrors) !== JSON.stringify(updatedFloorErrors)) {
        setFloorErrors(updatedFloorErrors);
      }
    }
  }, [errors, currentFloor, floorErrors, setFloorErrors, spaceProfileCollection])

  if ([DRAFT, UNPUBLISHED, TEAR_SHEET].includes(status)) {
    return (
      <>
        <Button variant="none" className={classNames('btn-prime', styles.publishBtn)} onClick={() => setShowConfirmModal(true)} disabled={publishButtonDisabled()}>
          {t('ProfilePublishButton.publish')}
        </Button>
        <Button variant="none" className={classNames('btn-prime', styles.previewBtn)} onClick={publishPreview}>
          {t('ProfilePublishButton.publishPreview')}
        </Button>
        <Button variant="none" className={classNames('btn-prime', styles.previewBtn)} onClick={() => setShowPreviewModal(true)}>
          {t('ProfilePublishButton.publishStatus')}
        </Button>
        <ConfirmModal show={showConfirmModal} title={t('ProfilePublishButton.publish.modalTitle')} onConfirm={() => publishProfile(PUBLISHED, status)} onHide={closeConfirmModalHandler}
          cancelButtonText={softRequiredErrors.length ? t('ui.no') : t('ui.cancel')} confirmButtonText={softRequiredErrors.length ? t('ui.yes') : t('ui.ok')}>
          {!softRequiredErrors.length ?
            t('ProfilePublishButton.publishModal.confirmMessage') :
            t('ProfilePublishButton.softRequired')
          }
        </ConfirmModal>
        <ConfirmModal show={showPreviewModal} title={t('ProfilePublishButton.publishStatus.modalTitle')} onConfirm={() => setShowPreviewModal(false)} 
          onHide={() => setShowPreviewModal(false)} cancelButtonText={t('ui.cancel')} confirmButtonText={t('ui.ok')}>
          {Object.keys(floorErrors).length === 0 ? (
            t('ProfilePublishButton.previewModal.confirmMessage')
          ) : (
            <>
              <p>{t('ProfilePublishButton.previewModal.errorsFound')}</p>
              <ul>
                {Object.entries(floorErrors).map(([floorNumber, errors]) => (
                  errors && (
                    <li key={floorNumber}>
                      {`Level: ${floorNumber}`}
                    </li>
                  )
                ))}
              </ul>
            </>
          )}
        </ConfirmModal>
      </>
    );
  }

  if (status === PUBLISHED) {
    const isDisabled = isSaving || !currentUser.is_id360_admin;
    const button = (
      <Button variant="none" className={classNames('btn-prime', styles.headerActionBtn, isDisabled ? styles.disabled : null)} onClick={() => setShowConfirmModal(true)} disabled={isDisabled}>
        {t('ProfilePublishButton.unpublish')}
      </Button>
    );
    const regeneratePdfButton = (
      <Button 
        variant="none" 
        className={classNames('ml-1', 'btn-prime', styles.previewBtn, isDisabled ? styles.disabled : null)} 
        onClick={regeneratePdf} 
        disabled={isDisabled || isRegeneratingPdf}
      >
        {isRegeneratingPdf ? 'Regenerating...' : 'Regenerate PDF'}
      </Button>
    );
    return (
      <>
        {!currentUser.is_id360_admin ?
          <OverlayTrigger placement={'bottom'} overlay={<Tooltip id="tooltip-disabled">{t('ProfilePublishButton.tooltip')}</Tooltip>}>
            <span className="d-inline-block">
              {button}
            </span>
          </OverlayTrigger>
          :
          button
        }
        {!currentUser.is_id360_admin ?
          <OverlayTrigger placement={'bottom'} overlay={<Tooltip id="tooltip-regenerate-disabled">{t('ProfilePublishButton.tooltip')}</Tooltip>}>
            <span className="d-inline-block">
              {regeneratePdfButton}
            </span>
          </OverlayTrigger>
          :
          regeneratePdfButton
        }
        <ConfirmModal show={showConfirmModal} title={t('ProfilePublishButton.unpublish.confirmLabel')} onConfirm={() => publishProfile(UNPUBLISHED, status)} onHide={() => setShowConfirmModal(false)}
          cancelButtonText={t('ui.cancel')} confirmButtonText={t('ui.ok')}>
          {t('ProfilePublishButton.unpublish.confirmModal')}
        </ConfirmModal>
      </>
    );
  }
}
